import { useState, type ReactNode } from 'react';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Empty, ErrorState, Skeleton } from '@/components/States';
import { DeskHead, Group, Cell, PhoneBar, ReadonlyNote } from './kit/Kit';
import { useApi } from './kit/hooks';
import { SETTINGS_ENTRIES, compactCount, useSettingsCounts } from './AdminLayout';
import { UploadMaintenance } from './UploadMaintenance';
import { SettingsEditor } from './SettingsEditor';
import type { SystemSettings } from './types';
import s from './SettingsHome.module.css';

/** Claude system.html sY1 / system-ipad.html Y1 / system-mobile.html Y1. */
export function SettingsHome() {
  const ff = useFormFactor();
  const can = useSession((state) => state.can);
  const [headerSave, setHeaderSave] = useState<ReactNode>(null);
  const readable = can('system.settings.read');
  const settings = useApi<SystemSettings>(readable ? '/api/settings' : null);
  const counts = useSettingsCounts(ff === 'phone');
  const content = !readable ? <Empty icon="lock" title="没有查看基本设置的权限">请联系管理员开通查看系统设置权限。</Empty>
    : settings.error ? <ErrorState title="系统设置加载失败" message={settings.error.message} detail="GET /api/settings" onRetry={settings.reload} />
      : settings.loading || !settings.data ? <div className={s.pad}><Skeleton /><Skeleton lines={5} /></div>
        : <SettingsEditor initial={settings.data} onSaved={settings.reload} />;

  if (ff === 'phone') {
    const entries = SETTINGS_ENTRIES.filter((entry) => entry.key !== 'basic' && !['roles', 'accounts', 'history', 'migration'].includes(entry.key) && can(...entry.perms));
    const cells = (group: string) => entries.filter((entry) => entry.group === group).map((entry) =>
      <Cell key={entry.key} icon={entry.icon} tone={entry.tone} label={entry.label} value={compactCount(counts[entry.key])} to={entry.to} />);
    const accountCount = [can('roles.read') ? counts.roles : undefined, can('users.read') ? counts.accounts : undefined]
      .filter((value): value is number => value !== undefined);
    return <div className={s.phone}>
      <PhoneBar back="/apps" backLabel="应用" title="系统设置" right={headerSave} />
      <div className={s.pscroll}>
        {entries.some((entry) => entry.group !== '审计') && <Group>
          {cells('设置')}
          {(can('roles.read') || can('users.read')) && <Cell icon="team" tone="blue" label="账号与角色" value={accountCount.length ? compactCount(accountCount.reduce((sum, value) => sum + value, 0)) : null} to="/users" />}
        </Group>}
        {entries.some((entry) => entry.group === '审计') && <Group title="审计">{cells('审计')}</Group>}
        {!readable ? content : settings.error ? content : settings.loading || !settings.data ? content
          : <SettingsEditor initial={settings.data} onSaved={settings.reload} onHeaderActionChange={setHeaderSave} />}
        {readable && <UploadMaintenance />}
        {import.meta.env.VITE_READONLY !== '0' && <ReadonlyNote />}
      </div>
    </div>;
  }

  return <div className={s.desk}>
    <DeskHead title="基本设置" sub="系统名称、账号安全、外观和提醒设置" />
    <div className={s.scroll}>{content}{readable && <UploadMaintenance />}{import.meta.env.VITE_READONLY !== '0' && <ReadonlyNote />}</div>
  </div>;
}
